import { Component, computed, DestroyRef, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ApiErrorBannerComponent } from '@app/shared/components/api-error-banner/api-error-banner.component';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { SelectUsersListComponent } from '@app/shared/components/select-users-list/select-users-list.component';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { LocalApiErrorHandler } from '@app/shared/services/local-api-error-handler.service';
import { ProjectParticipantsDataService } from '@app/shared/services/project-participants-data.service';
import { Group, SelectUsersAsProjectParticpantsDialogData } from '@app/shared/types/models/group/group.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { User } from '@app/shared/types/models/user/user.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize, Observable, tap } from 'rxjs';

@Component({
  selector: 'proffeo-select-users-as-project-particpants-dialog',
  imports: [
    MatDialogModule,
    TranslatePipe,
    UiDialogComponent,
    SelectUsersListComponent,
    UiButtonComponent,
    ApiErrorBannerComponent
  ],
  providers: [LocalApiErrorHandler],
  templateUrl: './select-users-as-project-particpants-dialog.component.html'
})
export class SelectUsersAsProjectParticpantsDialogComponent implements OnInit {
  private readonly projectParticipantsDataService: ProjectParticipantsDataService =
    inject(ProjectParticipantsDataService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly dialogRef: MatDialogRef<SelectUsersAsProjectParticpantsDialogComponent> = inject(
    MatDialogRef<SelectUsersAsProjectParticpantsDialogComponent>
  );
  private readonly dialogData: SelectUsersAsProjectParticpantsDialogData = inject(MAT_DIALOG_DATA);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly localApiErrorHandler: LocalApiErrorHandler = inject(LocalApiErrorHandler);

  protected readonly groupUsers: Signal<User[]> = computed(() =>
    this.group()?.id
      ? this.group()
          .users()
          .map(_ => _.applicationUser())
      : undefined
  );

  protected selectedUsers: WritableSignal<string[]> = signal<string[]>(undefined);
  protected preselectedUsers: WritableSignal<string[]> = signal<string[]>(undefined);
  protected lockedUser: WritableSignal<string> = signal<string>(undefined);
  protected group: WritableSignal<Group> = signal<Group>(undefined);

  protected selectedProject: Signal<Project> = this.activeProjectService.activeProject;

  public ngOnInit(): void {
    this.preselectedUsers.set(this.dialogData.preselectedUsers.map(user => user.id));
    this.lockedUser.set(this.dialogData.lockedUser?.id);
    this.selectedUsers.set([...this.preselectedUsers()]);
    this.group.set(this.dialogData.group);
  }

  protected save(): void {
    if (this.localApiErrorHandler.isSaving()) {
      return;
    }

    this.dialogRef.disableClose = true;
    this.localApiErrorHandler
      .execute(this.buildSaveRequests())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.dialogRef.disableClose = false;
        })
      )
      .subscribe(succeeded => {
        if (succeeded) {
          this.dialogRef.close();
        }
      });
  }

  private buildSaveRequests(): Observable<unknown>[] {
    const lockedUserId = this.lockedUser();
    const idsToAdd = this.selectedUsers().filter(id => !this.preselectedUsers().includes(id) && id !== lockedUserId);
    const idsToRemove = this.preselectedUsers().filter(id => !this.selectedUsers().includes(id) && id !== lockedUserId);
    const projectId = this.selectedProject()?.id;
    const localErrorHandling = { handleErrorLocally: true };
    const requests: Observable<unknown>[] = [];

    if (idsToAdd.length > 0 && projectId) {
      requests.push(
        this.projectParticipantsDataService
          .addUsersToGroup(projectId, this.dialogData.group, idsToAdd, localErrorHandling)
          .pipe(tap(() => this.preselectedUsers.update(ids => [...ids, ...idsToAdd])))
      );
    }

    if (idsToRemove.length > 0 && projectId) {
      requests.push(
        this.projectParticipantsDataService
          .removeUsersFromGroup(projectId, this.dialogData.group, idsToRemove, localErrorHandling)
          .pipe(tap(() => this.preselectedUsers.update(ids => ids.filter(id => !idsToRemove.includes(id)))))
      );
    }

    return requests;
  }
}
